/**
 * JL Utilities - Vanilla JS helpers for Justified Laboratories
 * Replaces jQuery dependency for Expanse theme migration
 * @version 1.0.0
 * @author Claude Code
 */

(function(global) {
  'use strict';

  // ============================================
  // QUERY HELPERS
  // ============================================

  /**
   * Query single element (like jQuery $())
   * @param {string} selector - CSS selector
   * @param {Element} [context=document] - Context to search within
   * @returns {Element|null}
   */
  const $ = (selector, context = document) => context.querySelector(selector);

  /**
   * Query all elements (like jQuery $() returning multiple)
   * @param {string} selector - CSS selector
   * @param {Element} [context=document] - Context to search within
   * @returns {Element[]}
   */
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  // ============================================
  // CLASS MANIPULATION
  // ============================================

  /**
   * Add class to element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} className - Class to add
   */
  const addClass = (target, className) => {
    const elements = normalizeElements(target);
    elements.forEach(el => el?.classList.add(className));
  };

  /**
   * Remove class from element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} className - Class to remove
   */
  const removeClass = (target, className) => {
    const elements = normalizeElements(target);
    elements.forEach(el => el?.classList.remove(className));
  };

  /**
   * Check if element has class
   * @param {Element} element - DOM element
   * @param {string} className - Class to check
   * @returns {boolean}
   */
  const hasClass = (element, className) => element?.classList.contains(className) ?? false;

  /**
   * Toggle class on element
   * @param {Element} element - DOM element
   * @param {string} className - Class to toggle
   * @param {boolean} [force] - Force add/remove
   * @returns {boolean}
   */
  const toggleClass = (element, className, force) => element?.classList.toggle(className, force);

  // ============================================
  // VISIBILITY
  // ============================================

  /**
   * Hide element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   */
  const hide = (target) => {
    const elements = normalizeElements(target);
    elements.forEach(el => {
      if (el) el.style.display = 'none';
    });
  };

  /**
   * Show element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} [display=''] - Display value (empty string resets to CSS default)
   */
  const show = (target, display = '') => {
    const elements = normalizeElements(target);
    elements.forEach(el => {
      if (el) el.style.display = display;
    });
  };

  /**
   * Check if element is visible
   * @param {Element} element - DOM element
   * @returns {boolean}
   */
  const isVisible = (element) => {
    if (!element) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };

  // ============================================
  // TEXT & ATTRIBUTES
  // ============================================

  /**
   * Set text content on element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} text - Text to set
   */
  const setText = (target, text) => {
    const elements = normalizeElements(target);
    elements.forEach(el => {
      if (el) el.textContent = text;
    });
  };

  /**
   * Set HTML content on element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} html - HTML to set
   */
  const setHtml = (target, html) => {
    const elements = normalizeElements(target);
    elements.forEach(el => {
      if (el) el.innerHTML = html;
    });
  };

  /**
   * Get attribute value
   * @param {Element} element - DOM element
   * @param {string} attr - Attribute name
   * @returns {string|null}
   */
  const getAttr = (element, attr) => element?.getAttribute(attr);

  /**
   * Set attribute value
   * @param {Element} element - DOM element
   * @param {string} attr - Attribute name
   * @param {string} value - Attribute value
   */
  const setAttr = (element, attr, value) => element?.setAttribute(attr, value);

  /**
   * Remove attribute
   * @param {Element} element - DOM element
   * @param {string} attr - Attribute name
   */
  const removeAttr = (element, attr) => element?.removeAttribute(attr);

  /**
   * Get/set input value
   * @param {Element} element - Form element
   * @param {string} [value] - Value to set (omit to get)
   * @returns {string|undefined}
   */
  const val = (element, value) => {
    if (!element) return undefined;
    if (value === undefined) return element.value;
    element.value = value;
  };

  // ============================================
  // EVENTS
  // ============================================

  /**
   * Add event listener to element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} [options] - addEventListener options
   */
  const on = (target, event, handler, options = {}) => {
    const elements = normalizeElements(target);
    elements.forEach(el => el?.addEventListener(event, handler, options));
  };

  /**
   * Remove event listener from element(s)
   * @param {string|Element|Element[]} target - Selector or element(s)
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  const off = (target, event, handler) => {
    const elements = normalizeElements(target);
    elements.forEach(el => el?.removeEventListener(event, handler));
  };

  /**
   * Trigger custom event
   * @param {Element} element - DOM element
   * @param {string} eventName - Event name
   * @param {Object} [detail] - Event detail data
   */
  const trigger = (element, eventName, detail = {}) => {
    if (!element) return;
    const event = new CustomEvent(eventName, { detail, bubbles: true });
    element.dispatchEvent(event);
  };

  // ============================================
  // SHOPIFY AJAX
  // ============================================

  /**
   * Fetch wrapper for Shopify AJAX API
   * @param {string} url - API endpoint
   * @param {Object} [options] - fetch options
   * @returns {Promise<Object>}
   */
  const shopifyFetch = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`);
      error.response = response;
      error.status = response.status;
      throw error;
    }

    return response.json();
  };

  /**
   * Add item to cart
   * CRITICAL: Includes selling_plan for Recharge subscriptions
   * @param {number|string} variantId - Product variant ID
   * @param {number} [quantity=1] - Quantity to add
   * @param {number|string|null} [sellingPlan=null] - Selling plan ID for subscriptions
   * @param {Object} [properties={}] - Line item properties
   * @returns {Promise<Object>}
   */
  const addToCart = async (variantId, quantity = 1, sellingPlan = null, properties = {}) => {
    const data = {
      id: parseInt(variantId, 10),
      quantity: parseInt(quantity, 10)
    };

    // CRITICAL: Include selling_plan for Recharge subscriptions
    if (sellingPlan) {
      data.selling_plan = parseInt(sellingPlan, 10);
    }

    if (Object.keys(properties).length > 0) {
      data.properties = properties;
    }

    return shopifyFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  };

  /**
   * Get current cart
   * @returns {Promise<Object>}
   */
  const getCart = () => shopifyFetch('/cart.js');

  /**
   * Update cart quantities
   * @param {Object} updates - { variantId: quantity } map
   * @returns {Promise<Object>}
   */
  const updateCart = (updates) => shopifyFetch('/cart/update.js', {
    method: 'POST',
    body: JSON.stringify({ updates })
  });

  /**
   * Change cart item quantity
   * @param {string} line - Line item key or index
   * @param {number} quantity - New quantity (0 to remove)
   * @returns {Promise<Object>}
   */
  const changeCart = (line, quantity) => shopifyFetch('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({ line, quantity })
  });

  /**
   * Clear entire cart
   * @returns {Promise<Object>}
   */
  const clearCart = () => shopifyFetch('/cart/clear.js', { method: 'POST' });

  // ============================================
  // URL HELPERS
  // ============================================

  /**
   * Get URL parameters as object
   * @returns {Object}
   */
  const getUrlParams = () => Object.fromEntries(new URLSearchParams(window.location.search));

  /**
   * Get single URL parameter
   * @param {string} key - Parameter name
   * @returns {string|null}
   */
  const getUrlParam = (key) => new URLSearchParams(window.location.search).get(key);

  /**
   * Update URL parameters without page reload
   * @param {Object} params - Parameters to set (null value removes param)
   * @param {boolean} [replace=false] - Replace history state instead of push
   */
  const updateUrl = (params, replace = false) => {
    const url = new URL(window.location);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });

    if (replace) {
      history.replaceState({}, '', url);
    } else {
      history.pushState({}, '', url);
    }
  };

  // ============================================
  // EXPANSE THEME INTEGRATION
  // ============================================

  /**
   * Execute callback when page is loaded (Expanse compatible)
   * @param {Function} handler - Callback function
   */
  const onPageLoaded = (handler) => {
    // Check if already loaded
    if (document.readyState === 'complete') {
      handler();
      return;
    }

    // Listen for Expanse page:loaded event
    document.addEventListener('page:loaded', handler, { once: true });

    // Fallback to DOMContentLoaded
    document.addEventListener('DOMContentLoaded', handler, { once: true });
  };

  /**
   * Dispatch cart refresh event (Expanse theme)
   */
  const refreshCart = () => {
    document.dispatchEvent(new CustomEvent('cart:build'));
  };

  /**
   * Listen for Expanse cart updated event
   * @param {Function} handler - Callback receiving cart data
   */
  const onCartUpdated = (handler) => {
    document.addEventListener('cart:updated', (evt) => handler(evt.detail));
  };

  /**
   * Listen for Expanse product added event
   * @param {Function} handler - Callback receiving product data
   */
  const onProductAdded = (handler) => {
    document.addEventListener('ajaxProduct:added', (evt) => handler(evt.detail));
  };

  /**
   * Listen for Expanse variant change event
   * @param {Function} handler - Callback receiving variant data
   */
  const onVariantChange = (handler) => {
    document.addEventListener('variant:change', (evt) => handler(evt.detail));
  };

  // ============================================
  // UTILITY HELPERS
  // ============================================

  /**
   * Normalize target to array of elements
   * @param {string|Element|Element[]} target
   * @returns {Element[]}
   */
  const normalizeElements = (target) => {
    if (typeof target === 'string') {
      return $$(target);
    }
    if (target instanceof Element) {
      return [target];
    }
    if (Array.isArray(target)) {
      return target;
    }
    return [];
  };

  /**
   * Debounce function calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in ms
   * @returns {Function}
   */
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Format price in cents to currency string
   * @param {number} cents - Price in cents
   * @param {string} [format='${{amount}}'] - Shopify money format
   * @returns {string}
   */
  const formatMoney = (cents, format = '${{amount}}') => {
    const amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  };

  // ============================================
  // EXPORT
  // ============================================

  const JL = {
    // Query
    $, $$,
    // Classes
    addClass, removeClass, hasClass, toggleClass,
    // Visibility
    hide, show, isVisible,
    // Content
    setText, setHtml, getAttr, setAttr, removeAttr, val,
    // Events
    on, off, trigger,
    // Shopify AJAX
    shopifyFetch, addToCart, getCart, updateCart, changeCart, clearCart,
    // URL
    getUrlParams, getUrlParam, updateUrl,
    // Expanse
    onPageLoaded, refreshCart, onCartUpdated, onProductAdded, onVariantChange,
    // Utilities
    normalizeElements, debounce, formatMoney
  };

  // Expose globally
  global.JL = JL;

  // Also support ES modules if available
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = JL;
  }

})(typeof window !== 'undefined' ? window : global);
